import { RolesSystem } from 'lib/enums/RolesSystem.enum';
import { createActionTypes } from 'lib/utils/createActionTypes';

export default class UserDucks {
	public static initialState: { [label: string]: any } = {
		id: '',
		name: '',
		lastname: '',
		email: '',
		token: '',
		avatar: '',
		roles: [],
		events: [],
		isLoading: false,
		resendEmail: ''
	};

	public static duckName = 'User';

	public static actionTypes: { [label: string]: string } = createActionTypes(
		{
			// These actions are used to check on event by it's :eventId
			isEventHost: 'is_event_host',
			isEventPromoter: 'is_event_promoter',
			isEventChecker: 'is_event_checker',
			// This actions are used to just check if user has somewhere role like this
			isAdmin: 'is_admin',
			isEventHostSomewhere: 'is_event_host_somewhere',
			isEventPromoterSomewhere: 'is_event_promoter_somewhere',
			isEventCheckerSomewhere: 'is_event_checker_somewhere',
			// Auth related actions
			setLoading: 'set_loading',
			getIsLoggedIn: 'get_is_loggedin_user',
			login: 'login_user',
			logout: 'logout_user',
			// get and set User
			getUser: 'get_user',
			setUser: 'set_user',
			getResendEmail: 'get_resend_email',
			setResendEmail: 'set_resend_email'
			// reloadToken: 'reload_token'
		},
		UserDucks.duckName,
	);

	public static getOwnState = (state): any | any[] =>
		state[UserDucks.duckName] || UserDucks.initialState;

	public static reducer(state = UserDucks.initialState, action) {
		switch (action.type) {
			case UserDucks.actionTypes.setUser:
				return {
					...state,
					...action.payload,
				};
			case UserDucks.actionTypes.setLoading:
				return {
					...state,
					isLoading: action.payload.isLoading,
				};
			case UserDucks.actionTypes.setResendEmail:
				return {
					...state,
					resendEmail: action.payload.resendEmail
				};
			default:
				return state;
		}
	}

	/**
	 *  Actions - getters
	 */

	public static getUser(
		state,
	): {
		id: string;
		name: string;
		lastname: string;
		email: string;
		avatar: string;
		stripeId?: string;
		stripeVerificationComplete?: boolean;
		roles: string[];
		events: any[];
	} {
		const localState = this.getOwnState(state);
		return {
			id: localState.id,
			name: localState.name,
			lastname: localState.lastname || '',
			email: localState.email,
			stripeId: localState.stripeId || null,
			stripeVerificationComplete: localState.stripeId || null,
			avatar: localState.avatar || '',
			roles: localState.roles || [],
			events: localState.events || [],
		};
	}

	public static getToken(state): string {
		return this.getOwnState(state).token;
	}

	public static isAuthenticated(state): boolean {
		return this.getOwnState(state).token !== '';
	}

	/**
	 * Check if user has host role attached
	 *
	 * @param state store state
	 */
	public static isEventHostSomewhere(state): boolean {
		const roles: string[] = this.getOwnState(state).roles;
		return roles.indexOf(RolesSystem.HOST) > -1;
	}

	/**
	 * Check if user has promoter role attached
	 *
	 * @param state store state
	 */
	public static isEventPromoterSomewhere(state): boolean {
		const roles: string[] = this.getOwnState(state).roles;
		return roles.indexOf(RolesSystem.PROMOTER) > -1;
	}

	/**
	 * Check if user has promoter role attached
	 *
	 * @param state store state
	 */
	public static isEventCheckerSomewhere(state): boolean {
		const roles: string[] = this.getOwnState(state).roles;
		return roles.indexOf(RolesSystem.CHECKER) > -1;
	}

	/**
	 * Check if user has admin role attached
	 *
	 * @param state store state
	 */
	public static isAdmin(state): boolean {
		const roles: string[] = this.getOwnState(state).roles;
		return roles.indexOf(RolesSystem.ADMIN) > -1;
	}

	/**
	 * Verify if the user is event host of :eventId
	 * In case you forgot to provide eventId you will always get false !
	 *
	 * @param state store state
	 * @param eventId ObjectID as string of Event
	 */
	public static isEventHost(state, eventId?: string): boolean {
		return this.hasRoleInEvent(state, RolesSystem.HOST, eventId);
	}

	/**
	 * Verify if the user is event host of :eventId
	 * In case you forgot to provide eventId you will always get false !
	 *
	 * @param state store state
	 * @param eventId ObjectID as string of Event
	 */
	public static isEventPromoter(state, eventId?: string): boolean {
		return this.hasRoleInEvent(state, RolesSystem.PROMOTER, eventId);
	}

	/**
	 * Verify if the user is event host of :eventId
	 * In case you forgot to provide eventId you will always get false !
	 *
	 * @param state store state
	 * @param eventId ObjectID as string of Event
	 */
	public static isEventChecker(state, eventId?: string): boolean {
		return this.hasRoleInEvent(state, RolesSystem.CHECKER, eventId);
	}

	public static hasRoleInEvent(state, roleId: RolesSystem, eventId?: string) {
		// @ToDo
		// @Refactor
		// This need to be refactored because of too big cyclomatic complexity
		// and O Notation..
		if (!eventId) {
			return false;
		}
		const events: any[] = this.getOwnState(state).events;

		let hasRole = false;
		let isEnd = false;
		let counter = 0;
		const max = Object.keys(events).length;

		if (max > 0 && events) {
			do {
				if (events[counter].id === eventId) {
					if (events[counter].roles.indexOf(roleId) > -1) {
						hasRole = true;
						isEnd = true;
					}
				}
				// max - 1 <---- diff between array index counting and lenght count ;)
				if (counter === max - 1) {
					isEnd = true;
				}
				counter++;
			} while (!isEnd);
		}

		return hasRole;
	}

	/**
	 * Used for retrieving state of provisioning the current user.
	 * You have to set loading to true all the time you logging in / manipulating current user
	 *
	 * @param state store state
	 */
	public static isLoading(state) {
		return this.getOwnState(state).isLoading;
	}

	public static getResendEmail(state) {
		return this.getOwnState(state).resendEmail;
	}

	/**
	 * Actions - setters
	 */

	/**
	 *  Used to set user instance after login is over here.
	 */
	public static setUser = (userObject: {
		id?: string;
		name?: string;
		lastname?: string;
		username?: string;
		token?: string;
		avatar?: string;
		events?: any[];
		stripeId?: string;
		stripeVerificationComplete?: boolean;
		roles?: string[];
	}) => dispatch => {
		dispatch({
			type: UserDucks.actionTypes.setUser,
			payload: {
				isLoading: false,
				...userObject,
				email: userObject.username,
			},
		});
	};

	/**
	 * Used to set isLoading state. e.g.: when you are logging in user.
	 */
	public static setLoading = (isLoading: boolean = true) => dispatch => {
		dispatch({
			type: UserDucks.actionTypes.setLoading,
			payload: {
				isLoading,
			},
		});
	};

	public static setResendEmail = (email: any) => dispatch => {
		dispatch({
			type: UserDucks.actionTypes.setResendEmail,
			payload: {
				resendEmail: email,
			},
		});
	};

	// public static reloadToken = () => {
	// 	const token = getCookie("token") || '';

	// 	const userCookie = getCookie("user") || undefined;
	// 	let user = {};

	// 	if(userCookie){
	// 		user = JSON.parse(userCookie);
	// 	}

	// 	return {
	// 		type:UserDucks.actionTypes.reloadToken,
	// 		payload:{token,user}
	// 	}
	// }
}



// WEBPACK FOOTER //
// ./src/stores/ducks/User.duck.ts